import { useEffect, useState } from "react";
import { Plus, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../../lib/api";
import { inputCls, FIELD_GROUPS } from "./shared";

const MODULES = [
  ["dashboard", "Dashboard", ["view"]],
  ["products", "Products", ["view", "create", "edit", "delete"]],
  ["categories", "Collections", ["view", "create", "edit", "delete"]],
  ["orders", "Orders", ["view", "edit"]],
  ["customers", "Customers", ["view"]],
];

const FIELD_LABELS = {
  details: "Basic details (name, SKU, collection, tags, fit)",
  content: "Content (description, fabric, wash care, SEO)",
  price: "Selling price",
  cost: "Cost price (financial)",
  stock: "Stock & size inventory",
  images: "Images",
  visibility: "Status & featured",
};

const EMPTY_PERMS = { dashboard: [], products: [], categories: [], orders: [], customers: [], product_fields: {} };

export function RolesTab() {
  const [roles, setRoles] = useState([]);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState("");
  const [perms, setPerms] = useState(EMPTY_PERMS);

  const load = () => api.get("/admin/roles").then((r) => setRoles(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  const open = (r) => {
    setEditing(r || "new");
    setName(r?.name || "");
    setPerms(r ? { ...EMPTY_PERMS, ...r.permissions, product_fields: { ...(r.permissions?.product_fields || {}) } } : EMPTY_PERMS);
  };

  const toggleAction = (module, action) => {
    const current = perms[module] || [];
    const next = current.includes(action) ? current.filter((a) => a !== action) : [...current, action];
    setPerms({ ...perms, [module]: next });
  };

  const setField = (group, level) => setPerms({ ...perms, product_fields: { ...perms.product_fields, [group]: level } });

  const save = async (e) => {
    e.preventDefault();
    try {
      const payload = { name, permissions: perms };
      if (editing === "new") await api.post("/admin/roles", payload);
      else await api.put(`/admin/roles/${editing.key}`, payload);
      toast.success("Role saved — applies to assigned staff immediately");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (r) => {
    if (!window.confirm(`Delete role "${r.name}"?`)) return;
    await api.delete(`/admin/roles/${r.key}`).then(load).catch((e) => toast.error(formatApiError(e)));
  };

  return (
    <div>
      <button data-testid="role-add-button" onClick={() => open(null)} className="vv-btn-dark mb-8"><Plus size={12} /> New Role</button>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {roles.map((r) => (
          <div key={r.key} data-testid={`role-card-${r.key}`} className="border border-vv-black/10 p-5 flex flex-col">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="font-medium text-sm">{r.name}</p>
                <p className="text-[10px] uppercase tracking-[0.2em] text-vv-stone mt-1">{r.builtin ? "Built-in" : "Custom"}</p>
              </div>
              <div className="flex">
                <button data-testid={`role-edit-${r.key}`} onClick={() => open(r)} className="p-2" aria-label="Edit role"><Pencil size={14} strokeWidth={1.5} /></button>
                {!r.builtin && (
                  <button data-testid={`role-delete-${r.key}`} onClick={() => remove(r)} className="p-2 text-vv-burgundy" aria-label="Delete role"><Trash2 size={14} strokeWidth={1.5} /></button>
                )}
              </div>
            </div>
            <div className="mt-4 space-y-1 text-[11px] text-vv-muted">
              {MODULES.map(([m, label]) => (
                <p key={m}><span className="text-vv-black">{label}:</span> {(r.permissions?.[m] || []).join(", ") || "no access"}</p>
              ))}
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-[80] flex items-start justify-center overflow-y-auto py-10 px-4">
          <div className="absolute inset-0 bg-vv-black/40" onClick={() => setEditing(null)} />
          <form data-testid="role-form" onSubmit={save} className="relative bg-white w-full max-w-2xl p-8 space-y-6">
            <div className="flex items-center justify-between">
              <p className="font-display text-2xl">{editing === "new" ? "New Role" : `Edit — ${editing.name}`}</p>
              <button type="button" onClick={() => setEditing(null)} aria-label="Close"><X size={20} strokeWidth={1.25} /></button>
            </div>
            <input data-testid="role-name" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Role name (e.g. Studio Assistant)" className={inputCls} />

            <div>
              <p className="vv-overline text-vv-muted mb-3">Module Access</p>
              <div className="space-y-2">
                {MODULES.map(([m, label, actions]) => (
                  <div key={m} className="flex flex-wrap items-center gap-x-6 gap-y-2 border border-vv-black/10 px-4 py-3">
                    <span className="text-sm w-28">{label}</span>
                    {actions.map((a) => (
                      <label key={a} className="flex items-center gap-2 text-[11px] uppercase tracking-[0.15em] text-vv-muted">
                        <input data-testid={`role-perm-${m}-${a}`} type="checkbox" checked={(perms[m] || []).includes(a)} onChange={() => toggleAction(m, a)} className="accent-[#111111]" />
                        {a}
                      </label>
                    ))}
                  </div>
                ))}
              </div>
            </div>

            <div>
              <p className="vv-overline text-vv-muted mb-3">Product Field Access</p>
              <div className="space-y-2">
                {Object.keys(FIELD_GROUPS).map((g) => (
                  <div key={g} className="flex flex-wrap items-center justify-between gap-2 border border-vv-black/10 px-4 py-3">
                    <span className="text-[12px]">{FIELD_LABELS[g]}</span>
                    <select data-testid={`role-field-${g}`} value={perms.product_fields?.[g] || "none"} onChange={(e) => setField(g, e.target.value)}
                      className="bg-transparent border border-vv-black/20 text-[11px] uppercase tracking-[0.15em] px-2 py-1.5 outline-none">
                      <option value="none">Hidden</option>
                      <option value="view">View only</option>
                      <option value="edit">Edit</option>
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <button data-testid="role-save-button" type="submit" className="vv-btn-dark w-full">Save Role</button>
          </form>
        </div>
      )}
    </div>
  );
}
